import { getProductInventory } from '@api/shopify.js';

/**
 * Normalizes a Shopify variant ID to a consistent string format.
 * 
 * Handles both GID format (gid://shopify/ProductVariant/123456789) and numeric IDs.
 * 
 * @param {string|number} variantId - Variant ID in any format
 * @returns {string} - Normalized string ID
 */
const normalizeVariantId = (variantId) => {
	if (typeof variantId === 'string' && variantId.includes('ProductVariant/')) {
		return variantId.split('ProductVariant/')[1];
	}
	return String(variantId);
};

/**
 * Checks inventory availability for all items in a bundle product.
 * 
 * A bundle product consists of multiple individual products that are sold together.
 * This function checks if ALL bundle items are in stock. If any single item is out
 * of stock, the entire bundle is considered unavailable.
 * 
 * @param {Array<Object>} bundleItems - Array of bundle item objects, each containing:
 *   - product_id: Shopify product ID
 *   - variant_id: Shopify variant ID
 * @param {string} context - Optional context string for logging (e.g., 'BundleProduct', 'ProductNostoUI')
 * @returns {Promise<boolean>} - Returns true if any bundle item is out of stock (OOS),
 *   false if all items are in stock. Returns true on error to prevent false positives.
 * 
 * @example
 * const bundleItems = [
 *   { product_id: 123, variant_id: 456 },
 *   { product_id: 789, variant_id: 101 }
 * ];
 * const isOutOfStock = await checkBundleItemsInventory(bundleItems, 'BundleProduct');
 */
export const checkBundleItemsInventory = async (bundleItems, context = 'Bundle') => {
	// Validate input: empty or invalid bundle items are treated as out of stock
	if (!bundleItems || !Array.isArray(bundleItems) || bundleItems.length === 0) {
		return true;
	}

	try {
		// Filter out invalid items missing required identifiers
		// This ensures we only process items with valid product_id and variant_id
		const validBundleItems = bundleItems.filter(item => 
			item && 
			item.product_id != null && 
			item.product_id !== undefined &&
			item.variant_id != null &&
			item.variant_id !== undefined
		);

		if (validBundleItems.length === 0) {
			console.warn(`${context}: No valid bundle items (all missing product_id or variant_id)`);
			return true; // Invalid bundle configuration - treat as OOS
		}

		// Get unique product IDs to avoid fetching duplicate product inventories
		// Multiple variants of the same product will share the same inventory data
		const uniqueProductIds = [...new Set(validBundleItems.map(item => item.product_id).filter(Boolean))];
		
		if (uniqueProductIds.length === 0) {
			console.warn(`${context}: No valid product IDs found`);
			return true;
		}
		
		// Fetch inventory for all products in parallel for better performance
		// Errors are caught individually to prevent one failure from blocking others
		const inventoryPromises = uniqueProductIds.map(productId => 
			getProductInventory(productId).catch(error => {
				console.error(`${context}: Error fetching inventory for product ${productId}:`, error);
				return null; // Return null to indicate fetch failure, handled below
			})
		);
		
		const inventoryResults = await Promise.all(inventoryPromises);
		
		// Build a map of variant IDs to inventory levels for efficient lookup
		// Variant IDs are normalized to handle different formats (GID vs numeric)
		const variantInventoryMap = new Map();
		let itemsChecked = 0;
		
		inventoryResults.forEach((result) => {
			if (result?.product?.variants?.nodes) {
				result.product.variants.nodes.forEach(variant => {
					// Normalize variant ID to handle Shopify's GID format
					const variantIdStr = normalizeVariantId(variant.id);
					
					// Store inventory with normalized string ID
					variantInventoryMap.set(variantIdStr, variant.quantityAvailable);
					
					// Also store numeric version if different from string
					// This handles cases where IDs might be stored as numbers vs strings
					const variantIdNum = Number(variantIdStr);
					if (!isNaN(variantIdNum) && variantIdNum.toString() !== variantIdStr) {
						variantInventoryMap.set(variantIdNum.toString(), variant.quantityAvailable);
					}
				});
			}
		});
		
		// Check each bundle item's inventory status
		// If ANY item is out of stock, the entire bundle is unavailable
		for (const item of validBundleItems) {
			// Normalize the bundle item's variant_id to match our inventory map format
			const variantIdStr = normalizeVariantId(item.variant_id);
			
			// Try to find inventory - check both string and numeric formats
			let inventory = variantInventoryMap.get(variantIdStr);
			
			if (inventory === undefined) {
				const variantIdNum = Number(variantIdStr);
				if (!isNaN(variantIdNum)) {
					inventory = variantInventoryMap.get(variantIdNum.toString());
				}
			}
			
			// If variant not found in inventory, assume it's out of stock
			// This handles cases where variants might have been deleted or are invalid
			if (inventory === undefined) {
				inventory = 0;
			}
			
			itemsChecked++;
			
			// Early return: if any item is out of stock, bundle is unavailable
			if (inventory <= 0) {
				return true;
			}
		}
		
		// Safety check: ensure we actually checked at least one item
		if (itemsChecked === 0) {
			console.warn(`${context}: No bundle items were checked`);
			return true; // Invalid state - treat as OOS
		}
		
		// All bundle items are in stock
		return false;
	} catch (error) {
		console.error(`${context}: Error checking bundle inventory:`, error);
		// Return true on error to prevent false positives
		// Better to show as unavailable than allow purchase of items we can't verify
		return true;
	}
};

